import React, { SyntheticEvent, useCallback, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Form, { Field, FormSection, FormFooter, ErrorMessage, Legend } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Flex, Stack, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { experiments } from '@/utils/experiment/manifest.ts';
import { useExperiment } from '@/utils/experiment/use-experiment.ts';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';

import { ANALYTICS_PREFIX } from '../../../common/constants';
import { PageProjectDetailsProps } from '../../../common/types';
import { useWizardConfiguration } from '../../../controllers';

import {
	JIRA_MAX_PROJECT_NAME_LENGTH,
	JIRA_MIN_PROJECT_NAME_LENGTH,
	PROJECT_NAME_TEXT_FIELD,
} from './constants';
import messages from './messages';
import { getTeamSpecificExamples } from './utils';

export const LeftHandSidePageProjectDetails: React.FC<PageProjectDetailsProps> = ({
	onClickSkip,
	onClickNext,
	currentProjectName,
	onProjectNameChange,
	defaultProjectName,
	submitButton,
	Header,
}) => {
	const { formatMessage } = useIntl();
	const { teamType } = useWizardConfiguration();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sparkSkipOnboarding = useExperiment(experiments.sparkSkipOnboardingWizard);

	const sparkSkipOnboardingEnrolled =
		sparkSkipOnboarding.type === 'enrolled' && sparkSkipOnboarding.value;

	// State for managing selected example button
	const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
	const [hasClickedExample, setHasClickedExample] = useState(false);
	const [loading, setLoading] = useState(false);

	const visualFeedback = checkFeatureFlagGate(featureFlags.signupVisualAlignmentFeedback);

	const handleOnSubmit = () => {
		const teamSpecificExamples = getTeamSpecificExamples(teamType);
		const selectedExampleText = teamSpecificExamples[selectedExampleIndex]?.defaultMessage;

		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_PREFIX}PageProjectDetailsSubmitButton`,
			eventType: 'ui',
			attributes: {
				teamType,
				hasClickedExample,
				selectedExampleIndex,
				selectedExampleText,
				submittedTextMatchesExample: currentProjectName === selectedExampleText,
			},
		}).fire();

		onClickNext();
	};

	const handleSkipButton = () => {
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_PREFIX}PageProjectDetailsSkipButton`,
			eventType: 'ui',
			attributes: {
				teamType,
				hasClickedExample,
				selectedExampleIndex,
			},
		}).fire();

		setLoading(true);
		if (onClickSkip) {
			onClickSkip();
		}
	};

	const handleExampleClick = useCallback(
		(exampleText: string, index: number) => {
			setSelectedExampleIndex(index);
			setHasClickedExample(true);
			onProjectNameChange(exampleText);
		},
		[onProjectNameChange],
	);

	const validateProjectName = useCallback(
		(value: string | undefined) => {
			// If the project name is undefined, we don't need to validate it since we use the default project name
			if (value === undefined) {
				return;
			}
			const trimmedValueLength = value?.trim().length ?? 0;
			if (trimmedValueLength < JIRA_MIN_PROJECT_NAME_LENGTH) {
				return formatMessage(messages.projectNameMinCharacterLengthError, {
					_minCharacters: JIRA_MIN_PROJECT_NAME_LENGTH,
					get minCharacters() {
						return this['_minCharacters'];
					},
					set minCharacters(value) {
						this['_minCharacters'] = value;
					},
				});
			}
			if (trimmedValueLength > JIRA_MAX_PROJECT_NAME_LENGTH) {
				return formatMessage(messages.projectNameMaxCharacterLengthError, {
					maxCharacters: JIRA_MAX_PROJECT_NAME_LENGTH,
				});
			}
		},
		[formatMessage],
	);

	const handleProjectNameChanged = useCallback(
		(onChange: (e: SyntheticEvent<HTMLInputElement>) => void) =>
			(e: SyntheticEvent<HTMLInputElement>) => {
				onProjectNameChange(e.currentTarget.value);
				onChange(e);
			},
		[onProjectNameChange],
	);

	const FormContainer =
		checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlibV2) && Header ? Stack : FormSection;

	const renderHeading = () => {
		if (checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlibV2) && Header) {
			return (
				<Header
					header={formatMessage(messages.spaceHeader)}
					subheader={formatMessage(messages.bodyLineOne)}
				/>
			);
		}

		const subtext = (
			<p css={subtextStyles}>
				<FormattedMessage {...messages.bodyLineOne} />
			</p>
		);

		return (
			<Stack space="space.150">
				<div css={[titleCss, visualFeedback && titleVisualFeedbackCss]}>
					<Heading as="h1" size="large">
						{formatMessage(messages.spaceHeader)}
					</Heading>
				</div>
				{subtext}
			</Stack>
		);
	};

	const renderProjectExamples = () => {
		const exampleContent = (
			<Flex gap="space.100" wrap="wrap" justifyContent="start">
				{getTeamSpecificExamples(teamType).map((example, index) => {
					const exampleText = formatMessage(example);
					const isSelected = (currentProjectName ?? '').trim() === exampleText.trim();
					return (
						<Button
							key={teamType}
							appearance="subtle"
							isSelected={isSelected}
							aria-pressed={isSelected}
							onClick={() => handleExampleClick(exampleText, index)}
							css={[
								buttonStyles,
								visualFeedback && buttonVisualFeedbackStyles,
								isSelected && selectedButtonStyles,
								!visualFeedback && !isSelected && unselectedButtonStyles,
								visualFeedback && isSelected && selectedButtonTextVisualFeedbackStyles,
							]}
						>
							{exampleText}
						</Button>
					);
				})}
			</Flex>
		);

		return (
			<Stack space="space.0">
				<div css={[exampleHeadingCss, visualFeedback && exampleHeadingVisualFeedbackCss]}>
					<Legend>{formatMessage(messages.projectExampleHeading)}</Legend>
				</div>
				{exampleContent}
			</Stack>
		);
	};

	const fieldLabel = formatMessage(messages.projectFormField);

	return (
		<Flex xcss={outerContainerStyles}>
			<Form onSubmit={handleOnSubmit}>
				{({ formProps }) => (
					<form {...formProps} noValidate>
						<Stack spread="space-between" xcss={containerStyles}>
							<Stack>
								{renderHeading()}
								<FormContainer>
									<Stack space="space.300">
										<div css={[fieldLabelCss, visualFeedback && fieldLabelVisualFeedbackCss]}>
											<Field
												name={PROJECT_NAME_TEXT_FIELD}
												label={fieldLabel}
												validate={validateProjectName}
											>
												{({ fieldProps, error, meta }) => (
													<>
														<TextField
															{...fieldProps}
															autoComplete="off"
															defaultValue={defaultProjectName}
															value={currentProjectName}
															onChange={handleProjectNameChanged(fieldProps.onChange)}
														/>
														{fieldProps.isInvalid && meta.touched && (
															<ErrorMessage>{error}</ErrorMessage>
														)}
													</>
												)}
											</Field>
										</div>
										{renderProjectExamples()}
									</Stack>
								</FormContainer>
							</Stack>
							<FormFooter>
								<div css={buttonGroupCss}>
									{sparkSkipOnboardingEnrolled && (
										<div css={[submitButtonCss, visualFeedback && submitButtonVisualFeedbackCss]}>
											<Button appearance="subtle" onClick={handleSkipButton} isDisabled={loading}>
												{loading ? (
													<Spinner
														testId="spinner"
														interactionName="load"
														label="Loading"
														size="medium"
													/>
												) : (
													formatMessage(messages.skipWizardBtn)
												)}
											</Button>
										</div>
									)}
									{submitButton ? (
										submitButton
									) : (
										<div css={[submitButtonCss, visualFeedback && submitButtonVisualFeedbackCss]}>
											<Button type="submit" appearance="primary" isDisabled={loading}>
												{formatMessage(messages.getStartedBtn)}
											</Button>
										</div>
									)}
								</div>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</Flex>
	);
};

const outerContainerStyles = xcss({
	height: '100%',
});
const containerStyles = xcss({
	height: '100%',
});

const buttonStyles = css({
	borderWidth: '1px !important',
	// eslint-disable-next-line local/ensure-wac-token-usage
	borderColor: '#BCBDC0 !important',
	borderRadius: `${token('radius.small', '2px')} !important`,
	borderStyle: 'solid !important',
	// Hard-coded values required because token values are being overridden / ignored
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// Match the Text component font stack (ui-sans-serif, "Segoe UI", Ubuntu...)
		fontFamily:
			"ui-sans-serif, 'Segoe UI', Ubuntu, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important",
		fontSize: '14px !important',
		fontWeight: 'bold !important',
	},
});

const unselectedButtonStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		// eslint-disable-next-line local/ensure-wac-token-usage
		color: '#626F86 !important',
	},
});

const selectedButtonStyles = css({
	borderColor: `${token('color.border.focused')} !important`,
});

const buttonVisualFeedbackStyles = css({
	borderRadius: '6px !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontWeight: `${token('font.weight.regular')} !important`,
		color: `${token('color.text.subtle')} !important`,
	},
});

const selectedButtonTextVisualFeedbackStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		color: `${token('color.text.selected')} !important`,
	},
});

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '32px',
		lineHeight: '38px',
	},
});

const titleVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontSize: '24px',
		lineHeight: '28px',
	},
});

const subtextStyles = css({
	font: token('font.body'),
	color: token('color.text.subtle'),
	margin: 0,
});

const exampleHeadingCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	legend: {
		font: token('font.body'),
		fontWeight: token('font.weight.bold'),
	},
});

const exampleHeadingVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	legend: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
});

const submitButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `${wacToken('font.charlie.text')} !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

const submitButtonVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
	},
});

const fieldLabelCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		font: token('font.body'),
		color: token('color.text.subtlest'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	div: {
		borderColor: `${token('color.border.input')} !important`,
	},
});

const fieldLabelVisualFeedbackCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderRadius: '6px',
		borderWidth: '1px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]:focus-within']: {
		borderWidth: '2px',
	},
});

const buttonGroupCss = css({
	display: 'flex',
	gap: token('space.100'),
});
